import { ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type {
  ResolvedConversationDescriptionOptions,
  ConversationDescriptionOptions,
} from '@super/shared-types'

/**
 * Multiselect dropdown for what a handoff action's summary output contains.
 * Controlled: `value` is the action's stored (partial) options; unchecked
 * state comes from `defaults`, so an untouched group stores nothing and the
 * action keeps its default output. Summary is always included and cannot be
 * unchecked.
 */

type OptionKey =
  | 'includeSummary'
  | 'includeContactDetails'
  | 'includeTranscript'
  | 'includeRecordingUrl'

const OPTIONS: Array<{ key: OptionKey; label: string; shortLabel: string }> = [
  {
    key: 'includeSummary',
    label: 'Conversation summary',
    shortLabel: 'Summary',
  },
  {
    key: 'includeContactDetails',
    label: 'Contact details',
    shortLabel: 'Contact details',
  },
  {
    key: 'includeTranscript',
    label: 'Conversation transcript',
    shortLabel: 'Transcript',
  },
  {
    key: 'includeRecordingUrl',
    label: 'Link to conversation recording',
    shortLabel: 'Recording link',
  },
]

interface ConversationDescriptionOptionsFieldsProps {
  defaults: ResolvedConversationDescriptionOptions
  value: ConversationDescriptionOptions | undefined
  onChange: (next: ConversationDescriptionOptions) => void
  disabled?: boolean
}

export function ConversationDescriptionOptionsFields({
  defaults,
  value,
  onChange,
  disabled = false,
}: ConversationDescriptionOptionsFieldsProps) {
  // Summary is always included (server forces it too) — never let stored or
  // legacy data uncheck it in the UI.
  const effective = { ...defaults, ...(value ?? {}), includeSummary: true }
  const selectedLabels = OPTIONS.filter(o => effective[o.key]).map(
    o => o.shortLabel,
  )

  const toggle = (key: OptionKey, checked: boolean) =>
    onChange({ ...(value ?? {}), [key]: checked })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-colors outline-none hover:bg-accent focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-border dark:bg-input/30 dark:hover:bg-input/50"
        >
          <span className="truncate">
            <span className="text-muted-foreground">Include: </span>
            {selectedLabels.join(', ')}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-64"
      >
        {OPTIONS.map(option => {
          const isSummary = option.key === 'includeSummary'
          return (
            <DropdownMenuCheckboxItem
              key={option.key}
              checked={effective[option.key]}
              // Summary is always included in phase 1.
              disabled={isSummary}
              onSelect={e => e.preventDefault()}
              onCheckedChange={
                isSummary
                  ? undefined
                  : checked => toggle(option.key, checked === true)
              }
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
